import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Award, TrendingUp, Calendar, Briefcase } from 'lucide-react';

/**
 * Inline section for ContractorsPage: lets the operator set the
 * quality tier, manual quality uplift %, and confirm the auto-computed
 * loyalty uplift % derived from start_date + total_jobs_completed.
 *
 * Pure-prop component — owns no fetches; the parent ContractorsPage holds
 * the `form` state and applies updates via `onChange(partial)`.
 */
export default function ContractorUpliftFields({ form, onChange, contractorId, api }) {
  const [breakdown, setBreakdown] = useState(null);

  // Pure client-side loyalty calc — mirrors backend `_calc_loyalty_percent`.
  // Used so the Effective % preview reflects unsaved form edits before the
  // contractor row is persisted (otherwise the API echoes stale DB values).
  const calcAutoLoyalty = (startDateIso, jobsCompleted) => {
    let years = 0;
    if (startDateIso) {
      try {
        const sd = new Date(String(startDateIso).slice(0, 10));
        if (!isNaN(sd.getTime())) {
          const today = new Date();
          years = today.getFullYear() - sd.getFullYear()
            - ((today.getMonth() < sd.getMonth() ||
                (today.getMonth() === sd.getMonth() && today.getDate() < sd.getDate())) ? 1 : 0);
          years = Math.max(0, years);
        }
      } catch { years = 0; }
    }
    const jobs = Math.max(0, parseInt(jobsCompleted, 10) || 0);
    const raw = years * 1.0 + Math.floor(jobs / 50) * 1.0;
    return Math.min(raw, 10.0);
  };

  // Fetch the persisted breakdown once on open — used only for the "last
  // saved" reference text; the live numbers below come from form state.
  useEffect(() => {
    let cancel = false;
    if (!contractorId) { setBreakdown(null); return; }
    api.get(`/contractors/${contractorId}/effective-uplift`).then(r => {
      if (!cancel) setBreakdown(r.data);
    }).catch(() => { if (!cancel) setBreakdown(null); });
    return () => { cancel = true; };
  }, [api, contractorId]);

  const tier = form.quality_tier || 'Bronze';
  const tierPercent = { Bronze: 0, Silver: 5, Gold: 10 }[tier] || 0;
  // Live client-side loyalty calc — reacts to form edits immediately.
  const autoLoyalty = calcAutoLoyalty(form.start_date, form.total_jobs_completed);
  const appliedLoyalty = form.loyalty_uplift_percent_override
    ? (parseFloat(form.loyalty_uplift_percent) || 0)
    : autoLoyalty;
  const total = (tierPercent + (parseFloat(form.quality_uplift_percent) || 0) + appliedLoyalty).toFixed(1);

  return (
    <div data-testid="contractor-uplift-section" className="space-y-4 bg-amber-50/40 border border-amber-100 rounded-xl p-4">
      <div className="flex items-center gap-2 text-amber-900">
        <Award size={14} />
        <h4 className="text-xs uppercase tracking-[0.14em] font-bold">Quality &amp; Loyalty Uplift</h4>
        <span className="ml-auto text-xs font-semibold text-amber-900">Effective: <span data-testid="contractor-uplift-total" className="text-[#0F4F1B]">{total}%</span></span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Quality Tier</label>
          <select
            data-testid="contractor-quality-tier"
            value={tier}
            onChange={e => onChange({ quality_tier: e.target.value })}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
          >
            <option value="Bronze">Bronze (+0%)</option>
            <option value="Silver">Silver (+5%)</option>
            <option value="Gold">Gold (+10%)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Manual Uplift (%)</label>
          <input
            data-testid="contractor-manual-uplift"
            type="number" step="0.5"
            value={form.quality_uplift_percent ?? ''}
            onChange={e => onChange({ quality_uplift_percent: e.target.value })}
            placeholder="0"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono-num"
          />
          <p className="text-[10px] text-slate-400 mt-0.5">Per-contractor override</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center gap-1"><Calendar size={11} /> Start Date</label>
          <input
            data-testid="contractor-start-date"
            type="date"
            value={form.start_date || ''}
            onChange={e => onChange({ start_date: e.target.value })}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center gap-1"><Briefcase size={11} /> Jobs Completed</label>
          <input
            data-testid="contractor-total-jobs"
            type="number" step="1" min="0"
            value={form.total_jobs_completed ?? 0}
            onChange={e => onChange({ total_jobs_completed: e.target.value })}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono-num"
          />
        </div>
      </div>

      <div className="flex items-start gap-3 text-xs bg-white border border-amber-100 rounded-lg p-3">
        <TrendingUp size={14} className="text-emerald-700 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-semibold text-slate-700">Auto Loyalty Uplift: <span data-testid="contractor-auto-loyalty" className="text-emerald-700">{autoLoyalty.toFixed(1)}%</span></p>
          <p className="text-slate-500 mt-0.5">
            +1% per year tenure, +1% per 50 completed jobs (capped at 10%).
            {' '}
            Auto-computed from <strong>{form.start_date || 'no start date'}</strong> &amp; <strong>{form.total_jobs_completed || 0} jobs</strong>.
            {contractorId && breakdown && (
              <span className="text-slate-400"> · Saved value: {(breakdown.total_percent || 0).toFixed(1)}%</span>
            )}
          </p>
          <label className="flex items-center gap-2 mt-2 text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              data-testid="contractor-loyalty-override"
              checked={!!form.loyalty_uplift_percent_override}
              onChange={e => onChange({ loyalty_uplift_percent_override: e.target.checked })}
              className="w-3.5 h-3.5 accent-[#D35400] rounded"
            />
            Manually override loyalty uplift
          </label>
          {form.loyalty_uplift_percent_override && (
            <input
              data-testid="contractor-loyalty-override-value"
              type="number" step="0.5"
              value={form.loyalty_uplift_percent ?? ''}
              onChange={e => onChange({ loyalty_uplift_percent: e.target.value })}
              placeholder="0"
              className="mt-1.5 w-32 px-2 py-1 border border-amber-200 rounded text-sm font-mono-num"
            />
          )}
        </div>
      </div>
    </div>
  );
}
